import { useEntryMotion } from '@/components/ui/charts';
import { cn } from '@/lib/utils/cn';
import { useHealthReadinessQuery } from '@/lib/api/health-readiness';
import { useScheduleMilestonesQuery } from '@/lib/api/health-schedule';
import { isNewEngagement } from './overview-model';
import {
  AiCallout,
  DomainMap,
  FindingsCard,
  GetStarted,
  PointsCard,
  RoadCard,
  TrendCard,
} from './overview-cards';
import { CountdownCard, PostureCard, ReadinessCard } from './verdict-cards';

/**
 * Health & Readiness → Overview (T-0242 / E22-F01; prototype `overviewTab()`),
 * top to bottom: the AI readings waiting; the three verdicts (program
 * posture, go-live readiness, days to go-live); readiness over time beside
 * where the points are, or, for a new engagement, the get-started card;
 * the domain tiles; the road to go-live beside the open findings.
 *
 * Reads the composite (the one request the header shares) and the
 * schedule list. Every number drawn is one the API computed; see
 * `overview-model.ts` for the little that happens here.
 */
export interface OverviewTabProps {
  engagementId: string;
  /** Today, for the countdown, the runway and the live trend point. */
  today?: Date;
}

export function OverviewTab({ engagementId, today = new Date() }: OverviewTabProps) {
  const { data: view } = useHealthReadinessQuery(engagementId);
  const schedule = useScheduleMilestonesQuery(engagementId);
  // Rows rise in on entry; nothing moves under reduced motion.
  const { animate } = useEntryMotion('overview');
  if (!view) return null;

  const fresh = isNewEngagement(view);
  const card = { engagementId, view };
  return (
    <div className={cn('flex flex-col gap-4', animate && 'chart-anim')} data-testid="overview-tab">
      <AiCallout {...card} />
      <div className="grid gap-4 lg:grid-cols-[1.15fr_1fr_1fr]">
        <PostureCard {...card} />
        <ReadinessCard view={view} />
        <CountdownCard
          view={view}
          milestones={schedule.milestones}
          loading={schedule.isLoading}
          today={today}
        />
      </div>
      {fresh ? (
        <GetStarted {...card} />
      ) : view.verdict.readiness ? (
        <div className="grid gap-4 lg:grid-cols-[1.65fr_1fr]">
          <TrendCard {...card} today={today} />
          <PointsCard {...card} />
        </div>
      ) : null}
      <DomainMap {...card} />
      <div className="grid gap-4 lg:grid-cols-[1.9fr_1fr]">
        <RoadCard
          {...card}
          milestones={schedule.milestones}
          loading={schedule.isLoading}
          today={today}
        />
        <FindingsCard {...card} />
      </div>
    </div>
  );
}
